"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import Link from "next/link";
import { CalendarClock, ChevronLeft, ChevronRight, Mic, MessageCircle, Video } from "lucide-react";
import type { AppLocale } from "@/core/i18n/config";
import type { AvailableSlot, AvailableSlotsResponse, ConsultationMode } from "@/features/booking";
import { cn } from "@/shared/lib/utils";
import type { PublicHealerSessionOption } from "../../types";
interface HealerBookingPreviewProps {
  bookHref: string;
  locale: AppLocale;
  sessionOptions: PublicHealerSessionOption[];
  slots: AvailableSlotsResponse | null;
}
const MODE_ICON: Record<ConsultationMode, typeof Video> = {
  VIDEO: Video,
  AUDIO: Mic,
  CHAT: MessageCircle
};
const MODE_LABEL: Record<ConsultationMode, {
  en: string;
  hi: string;
}> = {
  VIDEO: {
    en: "Video",
    hi: "वीडियो"
  },
  AUDIO: {
    en: "Audio",
    hi: "ऑडियो"
  },
  CHAT: {
    en: "Chat",
    hi: "चैट"
  }
};
const DAYS_PER_PAGE = 3;
function periodSlots(day: AvailableSlot[] | undefined, mode: ConsultationMode): AvailableSlot[] {
  return (day ?? []).filter(slot => slot.modes.includes(mode));
}
export function HealerBookingPreview(t0) {
  const $ = _c(30);
  const {
    bookHref,
    locale,
    sessionOptions,
    slots
  } = t0;
  const isHindi = locale === "hi";
  let modes;
  if ($[0] !== sessionOptions) {
    modes = new Set();
    sessionOptions.forEach(option => option.consultationModes.forEach(mode => modes.add(mode)));
    $[0] = sessionOptions;
    $[1] = modes;
  } else {
    modes = $[1];
  }
  let t1;
  if ($[2] !== modes) {
    t1 = modes.size > 0 ? Array.from(modes) : ["VIDEO"] as ConsultationMode[];
    $[2] = modes;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const availableModes = t1;
  const [selectedMode, setSelectedMode] = useState(availableModes[0]);
  const [dayPage, setDayPage] = useState(0);
  const days = slots?.days ?? [];
  const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
    const firstAvailable = days.findIndex(_temp);
    return firstAvailable >= 0 ? firstAvailable : 0;
  });
  const [selectedSlot, setSelectedSlot] = useState(null);
  let t2;
  if ($[4] !== selectedMode || $[5] !== sessionOptions) {
    t2 = sessionOptions.find(o_0 => o_0.isDefault && o_0.consultationModes.includes(selectedMode)) ?? sessionOptions.find(o => o.consultationModes.includes(selectedMode)) ?? sessionOptions[0] ?? null;
    $[4] = selectedMode;
    $[5] = sessionOptions;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const selectedOption = t2;
  const visibleDays = days.slice(dayPage * DAYS_PER_PAGE, dayPage * DAYS_PER_PAGE + DAYS_PER_PAGE);
  const selectedDay = days[selectedDayIndex];
  const morning = periodSlots(selectedDay?.slots.morning, selectedMode);
  const afternoon = periodSlots(selectedDay?.slots.afternoon, selectedMode);
  const evening = periodSlots(selectedDay?.slots.evening, selectedMode);
  const t3 = "rounded-3xl border border-border bg-card p-6 shadow-[0_12px_30px_rgba(14,42,58,0.06)]";
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CalendarClock className="h-5 w-5 text-primary" />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isHindi ? "\u0938\u0924\u094D\u0930 \u092C\u0941\u0915 \u0915\u0930\u0947\u0902" : "Book a Session";
  let t6;
  if ($[8] !== t5) {
    t6 = <div className="flex items-center gap-2">{t4}<h2 className="font-heading text-lg font-semibold text-foreground">{t5}</h2></div>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== availableModes || $[11] !== isHindi || $[12] !== selectedMode) {
    t7 = availableModes.length > 0 && <div className="mt-5"><p className="text-sm font-semibold text-foreground">{isHindi ? "\u0906\u092A \u0915\u093F\u0938 \u0924\u0930\u0939 \u0915\u093E \u0938\u0924\u094D\u0930 \u091A\u093E\u0939\u0947\u0902\u0917\u0947?" : "What type of session would you like?"}</p><div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">{availableModes.map(mode_0 => {
          const Icon = MODE_ICON[mode_0];
          const isActive = mode_0 === selectedMode;
          return <button key={mode_0} type="button" onClick={() => {
            setSelectedMode(mode_0);
            setSelectedSlot(null);
          }} className={cn("flex flex-col items-center gap-1.5 rounded-xl border px-3 py-3 text-xs font-semibold transition", isActive ? "border-primary bg-primary text-white shadow-sm" : "border-border text-muted-foreground hover:border-primary/40")}><Icon className="h-4 w-4" />{isHindi ? MODE_LABEL[mode_0].hi : MODE_LABEL[mode_0].en}</button>;
        })}</div></div>;
    $[10] = availableModes;
    $[11] = isHindi;
    $[12] = selectedMode;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== isHindi || $[15] !== selectedOption) {
    t8 = selectedOption && <div className="mt-6"><p className="text-sm font-semibold text-foreground">{isHindi ? "\u0938\u0924\u094D\u0930 \u0915\u0940 \u0905\u0935\u0927\u093F" : "Session Duration"}</p><div className="mt-2 flex items-center justify-between rounded-xl bg-muted px-4 py-3 text-sm"><span className="font-semibold text-primary">{selectedOption.durationMinutes} {isHindi ? "\u092E\u093F\u0928\u091F" : "mins"}, 1{" "}{isHindi ? "\u0938\u0924\u094D\u0930" : "session"}</span><span className="font-semibold text-foreground">₹{selectedOption.price} /{isHindi ? "\u0938\u0924\u094D\u0930" : "session"}</span></div></div>;
    $[14] = isHindi;
    $[15] = selectedOption;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = days.length > 0 && <div className="mt-6"><p className="text-sm font-semibold text-foreground">{isHindi ? "\u0909\u092A\u0932\u092C\u094D\u0927 \u0938\u094D\u0932\u0949\u091F \u0926\u0947\u0916\u0947\u0902" : "Check available slots"}</p><div className="mt-3 flex items-center gap-2"><button type="button" onClick={() => setDayPage(_temp2)} disabled={dayPage === 0} className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border text-muted-foreground transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-40" aria-label={isHindi ? "\u092A\u093F\u091B\u0932\u0947 \u0926\u093F\u0928" : "Previous days"}><ChevronLeft className="h-4 w-4" /></button><div className="grid flex-1 grid-cols-3 gap-2">{visibleDays.map(day_0 => {
          const globalIndex = days.indexOf(day_0);
          const isActive_0 = globalIndex === selectedDayIndex;
          const available = hasAnySlots(day_0.slots);
          return <button key={day_0.date} type="button" disabled={!available} onClick={() => {
            setSelectedDayIndex(globalIndex);
            setSelectedSlot(null);
          }} className={cn("rounded-lg border px-2 py-2 text-center text-xs font-semibold transition", isActive_0 ? "border-primary bg-primary text-white" : available ? "border-border text-foreground hover:border-primary/40" : "cursor-not-allowed border-border/60 text-muted-foreground/60")}><span className="block">{day_0.displayDate}</span><span className="mt-0.5 block text-[10px] font-normal opacity-80">{available ? `${countSlots(day_0.slots, selectedMode)} ${isHindi ? "\u0909\u092A\u0932\u092C\u094D\u0927" : "available"}` : isHindi ? "\u0905\u0928\u0941\u092A\u0932\u092C\u094D\u0927" : "not available"}</span></button>;
        })}</div><button type="button" onClick={() => setDayPage(p_0 => (p_0 + 1) * DAYS_PER_PAGE < days.length ? p_0 + 1 : p_0)} disabled={(dayPage + 1) * DAYS_PER_PAGE >= days.length} className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border text-muted-foreground transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-40" aria-label={isHindi ? "\u0905\u0917\u0932\u0947 \u0926\u093F\u0928" : "Next days"}><ChevronRight className="h-4 w-4" /></button></div><div className="mt-5 space-y-5"><SlotGroup label={isHindi ? "\u0926\u094B\u092A\u0939\u0930" : "NOON"} slots={morning} selectedSlot={selectedSlot} onSelect={setSelectedSlot} /><SlotGroup label={isHindi ? "\u0936\u093E\u092E" : "EVENING"} slots={[...afternoon, ...evening]} selectedSlot={selectedSlot} onSelect={setSelectedSlot} /></div></div>;
  const t10 = !selectedSlot;
  const t11 = selectedSlot ? "bg-primary text-white hover:bg-primary/90" : "bg-muted text-muted-foreground";
  let t12;
  if ($[17] !== t11) {
    t12 = cn("mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3.5 text-sm font-semibold uppercase shadow-sm transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none", t11);
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = isHindi ? "\u0906\u0917\u0947 \u092C\u0922\u093C\u0947\u0902" : "Proceed";
  let t14;
  if ($[19] !== bookHref || $[20] !== t10 || $[21] !== t12 || $[22] !== t13) {
    t14 = <Link href={bookHref} aria-disabled={t10} className={t12}>{t13}</Link>;
    $[19] = bookHref;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t15 = <div className={t3}>{t6}{t7}{t8}{t9}{t14}</div>;
    $[24] = t14;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}
function _temp2(p) {
  return Math.max(0, p - 1);
}
function _temp(day) {
  return hasAnySlots(day.slots);
}
function hasAnySlots(slots: AvailableSlotsResponse["days"][number]["slots"]): boolean {
  return slots.morning.length + slots.afternoon.length + slots.evening.length > 0;
}
function countSlots(slots: AvailableSlotsResponse["days"][number]["slots"], mode: ConsultationMode): number {
  return periodSlots(slots.morning, mode).length + periodSlots(slots.afternoon, mode).length + periodSlots(slots.evening, mode).length;
}
function SlotGroup(t0) {
  const $ = _c(20);
  const {
    label,
    slots,
    selectedSlot,
    onSelect
  } = t0;
  if (slots.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-xs font-bold tracking-wide text-foreground uppercase">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = slots[slots.length - 1];
  let t3;
  if ($[2] !== slots[0].displayTime || $[3] !== t2.displayTime) {
    t3 = <p className="text-xs text-muted-foreground">{slots[0].displayTime} - {t2.displayTime}</p>;
    $[2] = slots[0].displayTime;
    $[3] = t2.displayTime;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <div className="flex items-baseline justify-between">{t1}{t3}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onSelect || $[9] !== selectedSlot?.start || $[10] !== slots) {
    let t6;
    if ($[12] !== onSelect || $[13] !== selectedSlot?.start) {
      t6 = slot => <button key={slot.start} type="button" onClick={() => onSelect(slot)} className={cn("rounded-lg border px-3 py-1.5 text-xs font-medium transition", selectedSlot?.start === slot.start ? "border-primary bg-primary text-white" : "border-border text-foreground hover:border-primary/40")}>{slot.displayTime}</button>;
      $[12] = onSelect;
      $[13] = selectedSlot?.start;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = slots.map(t6);
    $[8] = onSelect;
    $[9] = selectedSlot?.start;
    $[10] = slots;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <div className="mt-2 flex flex-wrap gap-2">{t5}</div>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t4 || $[18] !== t6) {
    t7 = <div>{t4}{t6}</div>;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}